import { useState, useEffect } from "react";
import type { Product } from "../../../mocks/products";
import { CATEGORIES } from "../../../mocks/products";

interface ProductModalProps {
  product: Product | null;
  onSave: (product: Product) => void;
  onClose: () => void;
}

const EMPTY: Omit<Product, "id"> = {
  name: "", brand: "", category: "smartphone",
  price: 0, originalPrice: 0, discount: 0,
  rating: 4.5, reviewCount: 0, image: "", description: "",
};

export default function ProductModal({ product, onSave, onClose }: ProductModalProps) {
  const isEdit = product !== null;
  const [form, setForm] = useState<Omit<Product, "id">>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (product) { const { id: _id, ...rest } = product; setForm(rest); }
    else setForm(EMPTY);
    setErrors({});
  }, [product]);

  const handlePriceChange = (field: "price" | "originalPrice", val: number) => {
    setForm((prev) => {
      const next = { ...prev, [field]: val };
      if (next.originalPrice > 0 && next.price > 0)
        next.discount = Math.round(((next.originalPrice - next.price) / next.originalPrice) * 100);
      return next;
    });
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "請輸入商品名稱";
    if (!form.brand.trim()) errs.brand = "請輸入品牌";
    if (form.price <= 0) errs.price = "請輸入正確現價";
    if (form.originalPrice <= 0) errs.originalPrice = "請輸入正確原價";
    if (form.originalPrice < form.price) errs.originalPrice = "原價不能低於現價";
    if (!form.image.trim()) errs.image = "請輸入圖片網址";
    return errs;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    onSave({ id: product?.id ?? `p${Date.now()}`, ...form });
  };

  const inputBase = "w-full h-10 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-[#1B4332] focus:bg-white transition-colors duration-200";
  const labelBase = "block text-xs font-medium text-gray-500 mb-1.5";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1B4332] text-white rounded-lg">
              <i className={`text-sm ${isEdit ? "ri-edit-line" : "ri-add-line"}`}></i>
            </div>
            <h2 className="text-gray-800 font-bold">{isEdit ? "編輯商品" : "新增商品"}</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-5 space-y-4"
          style={{ scrollbarWidth: "thin" }}
        >
          {/* Name */}
          <div>
            <label className={labelBase}>商品名稱 <span className="text-red-400">*</span></label>
            <input type="text" value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="例：iPhone 15 Pro Max 256GB"
              className={`${inputBase} ${errors.name ? "border-red-300" : ""}`}
            />
            {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Brand + Category */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelBase}>品牌 <span className="text-red-400">*</span></label>
              <input type="text" value={form.brand}
                onChange={(e) => setForm((p) => ({ ...p, brand: e.target.value.toUpperCase() }))}
                placeholder="APPLE"
                className={`${inputBase} ${errors.brand ? "border-red-300" : ""}`}
              />
              {errors.brand && <p className="text-red-400 text-xs mt-1">{errors.brand}</p>}
            </div>
            <div>
              <label className={labelBase}>分類</label>
              <div className="relative">
                <select value={form.category}
                  onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                  className={`${inputBase} pr-8 appearance-none cursor-pointer`}
                >
                  {CATEGORIES.filter((c) => c.id !== "all").map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm"></i>
              </div>
            </div>
          </div>

          {/* Price / OriginalPrice / Discount */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelBase}>現價 (NT$) <span className="text-red-400">*</span></label>
              <input type="number" value={form.price || ""}
                onChange={(e) => handlePriceChange("price", Number(e.target.value))}
                placeholder="42900" min={0}
                className={`${inputBase} ${errors.price ? "border-red-300" : ""}`}
              />
              {errors.price && <p className="text-red-400 text-xs mt-1">{errors.price}</p>}
            </div>
            <div>
              <label className={labelBase}>原價 (NT$) <span className="text-red-400">*</span></label>
              <input type="number" value={form.originalPrice || ""}
                onChange={(e) => handlePriceChange("originalPrice", Number(e.target.value))}
                placeholder="49900" min={0}
                className={`${inputBase} ${errors.originalPrice ? "border-red-300" : ""}`}
              />
              {errors.originalPrice && <p className="text-red-400 text-xs mt-1">{errors.originalPrice}</p>}
            </div>
            <div>
              <label className={labelBase}>折扣 (%)</label>
              <div className={`${inputBase} flex items-center justify-between bg-gray-50`}>
                <span className="text-gray-600 font-medium">{form.discount}%</span>
                <span className="text-gray-400 text-xs">自動計算</span>
              </div>
            </div>
          </div>

          {/* Rating + ReviewCount */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelBase}>評分 (1–5)</label>
              <input type="number" value={form.rating}
                onChange={(e) => setForm((p) => ({ ...p, rating: parseFloat(e.target.value) }))}
                step={0.1} min={1} max={5} className={inputBase}
              />
            </div>
            <div>
              <label className={labelBase}>評論數</label>
              <input type="number" value={form.reviewCount}
                onChange={(e) => setForm((p) => ({ ...p, reviewCount: Number(e.target.value) }))}
                min={0} placeholder="1000" className={inputBase}
              />
            </div>
          </div>

          {/* Image */}
          <div>
            <label className={labelBase}>圖片網址 <span className="text-red-400">*</span></label>
            <input type="text" value={form.image}
              onChange={(e) => setForm((p) => ({ ...p, image: e.target.value }))}
              placeholder="https://..."
              className={`${inputBase} ${errors.image ? "border-red-300" : ""}`}
            />
            {errors.image && <p className="text-red-400 text-xs mt-1">{errors.image}</p>}
            {form.image && !errors.image && (
              <div className="mt-2 w-16 h-16 rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                <img src={form.image} alt="preview" className="w-full h-full object-cover object-top" />
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className={labelBase}>商品描述</label>
            <input type="text" value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="簡短描述商品特色..." className={inputBase}
            />
          </div>

          {/* Footer buttons */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 h-11 flex items-center justify-center border border-gray-200 text-gray-500 text-sm font-medium hover:bg-gray-50 transition-all duration-200 rounded-xl cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
            <button type="submit"
              className="flex-1 h-11 flex items-center justify-center bg-[#1B4332] text-white text-sm font-semibold hover:bg-[#2D6A4F] transition-colors duration-200 rounded-xl cursor-pointer whitespace-nowrap"
            >
              <i className={`mr-2 text-sm ${isEdit ? "ri-save-line" : "ri-add-line"}`}></i>
              {isEdit ? "儲存變更" : "新增商品"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
